import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, take, timeout} from 'rxjs/operators';
import {LoginApiService} from '@shared/services/api/login-page/login-api.service';
import {NotifyService} from '@shared/services/global/notify.service';
import {AuthService, parseAuthData} from './auth.service';
import {joinedPagesHaveBusinesses} from './page-business.utils';

/** Lỗi HTTP tạm thời (mất mạng, máy chủ lỗi) — ApiService.handleError gắn statusCode, phản hồi của BE không có field này */
function isTransientHttpError(response: any): boolean {
  if (response?.statusCode === undefined || response?.statusCode === null) return false;
  const status = Number(response.statusCode);
  return status === 0 || status >= 500;
}

/** Thông báo BE trả về (messages là mảng hoặc chuỗi) */
function getResponseMessages(response: any): string[] {
  const messages = Array.isArray(response?.messages) ? response.messages : [response?.messages];
  return messages.filter((message: unknown): message is string => typeof message === 'string' && message.trim() !== '');
}

/**
 * Kết thúc session Page nhưng vẫn giữ session tài khoản.
 *
 * Tương đương `PageService.outPages()` của FE1: dùng cho lỗi token Page/Zalo
 * khi gửi tin, không dùng `AuthService.logout()` để tránh đăng xuất toàn bộ user.
 */
@Injectable({providedIn: 'root'})
export class PageSessionService {
  private readonly loginApi = inject(LoginApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notifyService = inject(NotifyService);
  private isEnding = false;
  private endingPageSession$: Observable<boolean> | null = null;
  private hasVerifiedActivePageSession = false;
  private verifyActivePageSession$: Observable<boolean> | null = null;

  constructor() {
    this.authService.onAuthCleared$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.hasVerifiedActivePageSession = false;
      this.verifyActivePageSession$ = null;
      this.isEnding = false;
      this.endingPageSession$ = null;
    });
  }

  /**
   * `usr` chỉ là cache FE; session Page ở BE có thể mất sau khi refresh server
   * hoặc sau khi mở lại tab. Phiên BE còn đúng user và đúng các page đã chọn thì
   * cho vào luôn; mất thì khôi phục bằng đúng API joinpages trước khi cho vào khu
   * vực hội thoại để BE không nhận `active page = null`.
   */
  public ensureActivePageSession(): Observable<boolean> {
    if (!this.authService.isLoginEd()) return of(false);
    if (this.hasVerifiedActivePageSession) {
      return of(true);
    }

    if (this.verifyActivePageSession$) {
      return this.verifyActivePageSession$;
    }

    const pageIds = this.authService.getPageIds()
      .map(pageId => String(pageId))
      .filter(Boolean);
    if (!pageIds.length) {
      return of(false);
    }

    const token = this.authService.getAuthorizationToken();
    this.verifyActivePageSession$ = this.hasActiveBackendPageSession(pageIds, token).pipe(
      switchMap(isActive => {
        if (!isActive) {
          return this.restorePageSession(pageIds, token);
        }
        this.hasVerifiedActivePageSession = true;
        return of(true);
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.verifyActivePageSession$;
  }

  /**
   * Kiểm tra nhanh qua /auth/me (chỉ đọc session) thay cho joinpages (gọi FB/Zalo, ghi Elastic — vài giây),
   * tránh treo ở trang login mỗi lần mở app. Page trong cache chưa gắn DN (khách từ V1) vẫn đi joinpages để BE chặn như cũ.
   */
  private hasActiveBackendPageSession(pageIds: string[], token: string): Observable<boolean> {
    if (!joinedPagesHaveBusinesses({pages: this.authService.getAllPages(), pageIds})) {
      return of(false);
    }

    return this.loginApi.authMe().pipe(
      take(1),
      map(response => {
        if (!this.authService.isLoginEd() || token !== this.authService.getAuthorizationToken()) return false;
        const auth = Number(response?.code) === 1 ? parseAuthData(response?.data) : null;
        if (!auth?.jwt || !auth.uid || String(auth.uid) !== String(this.authService.getUid())) return false;

        // PHP xuất mảng có key không liên tục thành object → đọc cả 2 dạng
        const backendPageIds = new Set(Object.values(auth.pageIds ?? {}).map(String));
        if (backendPageIds.size !== new Set(pageIds).size || !pageIds.every(pageId => backendPageIds.has(pageId))) {
          return false;
        }

        // Cùng user nhưng csrf phiên BE khác bản cache (VD: đăng nhập lại ở tab khác) → đồng bộ để request sau không 403
        if (`Bearer ${auth.jwt}` !== token) {
          this.authService.setJwt(auth.jwt);
        }
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /** Phiên BE mất page → khôi phục bằng joinpages như khi chọn page. */
  private restorePageSession(pageIds: string[], token: string): Observable<boolean> {
    return this.loginApi.userJoinPages({ids: pageIds, affiliate: ''}).pipe(
      take(1),
      switchMap((response: any) => {
        if (!this.authService.isLoginEd() || token !== this.authService.getAuthorizationToken()) return of(false);
        // Mất mạng / máy chủ lỗi: không đẩy người dùng ra màn chọn page chỉ vì lỗi tạm thời
        if (isTransientHttpError(response)) {
          return of(this.keepPagesUnverified());
        }

        const data = response?.data && typeof response.data === 'object'
          ? response.data
          : response;
        const code = Number(data?.code ?? response?.code);
        const restoredPageIds = Array.isArray(data?.pageIds)
          ? data.pageIds.map((pageId: string | number) => String(pageId)).filter(Boolean)
          : Array.isArray(data?.pages)
            ? data.pages.map((page: {id?: string | number}) => String(page?.id ?? '')).filter(Boolean)
            : [];

        if (code === 10 || code === 11 || (code === 1 && restoredPageIds.length && !joinedPagesHaveBusinesses(data))) {
          return this.rejectPagesWithoutBusiness();
        }

        if (code !== 1 || !restoredPageIds.length) {
          // BE từ chối (giới hạn giờ, page hết hạn gói, tài khoản bị khoá...) → báo lý do rồi mới về màn chọn page
          const messages = getResponseMessages(response);
          this.notifyService.warning(messages.length ? messages : 'login.pageSessionEnded');
          this.authService.outPages();
          return of(false);
        }

        this.authService.updateAuthorization(data);
        this.authService.isSelectPage = true;
        this.hasVerifiedActivePageSession = true;
        return of(true);
      }),
      catchError(error => {
        console.error('PageSessionService: lỗi khi khôi phục page', error);
        return of(this.keepPagesUnverified());
      })
    );
  }

  /**
   * Chưa xác nhận được phiên BE vì lỗi tạm thời → vẫn cho dùng page đang có nhưng không đánh dấu đã xác nhận:
   * lần điều hướng sau (AuthGuard) hoặc /auth/tracking sẽ kiểm tra lại.
   */
  private keepPagesUnverified(): boolean {
    this.verifyActivePageSession$ = null;
    return true;
  }

  /**
   * Phiên BE mất page giữa chừng (do /auth/tracking báo) trong khi FE vẫn đang dùng page: kiểm tra lại đúng như
   * AuthGuard lúc mở app (/auth/me → joinpages nếu cần); không khôi phục được thì về màn chọn page.
   */
  public restoreLostPageSession(): void {
    if (this.isEnding || !this.authService.isLoginEd()) return;
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;

    this.ensureActivePageSession().subscribe(isRestored => {
      if (!isRestored && this.authService.isLoginEd()) {
        void this.router.navigate(['/login']);
      }
    });
  }

  /** Join page vừa hoàn tất trong phiên hiện tại, không cần gọi lại API. */
  public markActivePageSessionVerified(): void {
    if (!this.authService.isLoginEd()) return;
    this.hasVerifiedActivePageSession = true;
    this.verifyActivePageSession$ = null;
  }

  /** Chặn session V1 chưa gắn DN; giữ đăng nhập và chờ BE thoát Page trước khi điều hướng. */
  public rejectPagesWithoutBusiness(): Observable<boolean> {
    if (this.endingPageSession$) return this.endingPageSession$;
    if (!this.authService.isLoginEd()) return of(false);
    this.notifyService.warning('login.pageBusinessRequired');
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;
    this.authService.outPages();
    this.endingPageSession$ = this.loginApi.logOutPage().pipe(
      take(1),
      timeout(15000),
      catchError(() => of(null)),
      map(() => false),
      finalize(() => { this.endingPageSession$ = null; }),
      shareReplay({bufferSize: 1, refCount: false})
    );
    return this.endingPageSession$;
  }

  public endPageSession(): void {
    if (this.isEnding || !this.authService.isLoginEd()) return;
    this.isEnding = true;

    this.loginApi.logOutPage()
      .pipe(
        take(1),
        // Dù BE phản hồi lỗi vẫn phải xoá state Page local để không kẹt màn hình chat.
        finalize(() => this.finish())
      )
      .subscribe({error: () => undefined});
  }

  private finish(): void {
    this.isEnding = false;
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;
    if (!this.authService.isLoginEd()) return;
    this.authService.outPages();
    void this.router.navigate(['/login']);
  }
}
